import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Users, 
  Building, 
  DollarSign,
  TrendingUp,
  FileText,
  Calendar,
  CreditCard
} from 'lucide-react';

const Institutional = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('personnel');

  const tabs = [
    { id: 'personnel', name: t('institutional.personnel'), icon: Users, href: '/institutional/personnel' },
    { id: 'other', name: t('institutional.other'), icon: Building, href: '/institutional/other' },
  ];

  const stats = [
    {
      title: t('institutional.totalPersonnel'),
      value: '12',
      icon: Users,
      color: 'bg-blue-500',
      change: '+2 this month'
    },
    {
      title: t('institutional.monthlySalaries'),
      value: '45,000 DA',
      icon: DollarSign,
      color: 'bg-green-500',
      change: '+5% from last month'
    },
    {
      title: t('institutional.otherExpenses'),
      value: '12,500 DA',
      icon: Building,
      color: 'bg-purple-500',
      change: '-10% from last month'
    },
    {
      title: t('institutional.totalPayments'),
      value: '156',
      icon: FileText,
      color: 'bg-orange-500',
      change: '+12 this month'
    }
  ];

  return (
    <>
      <div className="flex flex-col h-full" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
        {/* Fixed Header Section */}
        <div className="flex-shrink-0">
          {/* Header - Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('institutional.title')}</h1>
              <p className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-600">{t('institutional.subtitle')}</p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-4">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    <p className="text-xs text-gray-500">{stat.change}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-4">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6" aria-label="Tabs">
                {tabs.map((tab) => (
                  <a
                    key={tab.id}
                    href={tab.href}
                    className={`${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors`}
                  >
                    <tab.icon className="w-4 h-4 mr-2" />
                    {tab.name}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </div>

        {/* Scrollable Content Section */}
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 ml-3">Personnel Payments</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Manage salaries, bonuses, and payments for coaches and staff members.
              </p>
              <a
                href="/institutional/personnel"
                className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <Users className="w-4 h-4 mr-2" />
                Manage Personnel
              </a>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Building className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 ml-3">Other Expenses</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Track equipment purchases, maintenance, utilities, and other institutional expenses.
              </p>
              <a
                href="/institutional/other"
                className="inline-flex items-center px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
              >
                <Building className="w-4 h-4 mr-2" />
                Manage Expenses
              </a>
            </div>
          </div>
        </div>

        {/* Empty footer for spacing - sticky at bottom */}
        <div className="mt-6 bg-transparent p-4 flex-shrink-0"></div>
      </div>
    </>
  );
};

export default Institutional;







